import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import styled from 'styled-components';

import { Option } from 'types/common.types';
import { useUserInformation } from 'graphQL/Zustand/useUserInformation';

import {
  BookingConnection,
  BookingEdge,
  BookingTicket,
  FixedTicketEdge,
  TicketEdge,
  TicketOrderType,
  TicketState,
  UnfixedTicket,
} from 'types/api';
import { FixedTicket } from 'types/api';

import { GET_BOOKING_LIST, GET_LIST_VIEW_TICKET_LIST } from './graphQL';
import { fetchAllAtOnce } from 'utils/fetchAllAtOnce';
import { fetchWithRetry } from 'utils/fetchWithRetry';

import HackleManager from 'utils/hackle';

import dayjs from 'dayjs';

import { TicketFilterInput } from './hooks/useBookingListHooks';
import { BookingTermType } from './graphQL/types';
import Loading from 'components/Loading/Loading';
import ListViewTicketCard from './components/ListViewTicketCard';
import EmptyList from './components/EmptyList';
import SearchInput from 'components/SearchInput/SearchInput';
import RefreshButton from 'components/RefreshButton/RefreshButton';

const BookingListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const BookingList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 48px;

  padding: 0;
  margin: 0;
  margin-bottom: 64px;

  > li {
    display: flex;
  }
`;

const LoadingBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 300px;
`;

const convertTimestampToDate = timestamp => {
  return dayjs(timestamp).format('YYYY년 M월 D일 ddd요일');
};

interface BookingItemsFilter {
  page: number;
  size: number;
  filter: {
    hostId: string;
    customerSearchKeyword: string | null;
    scheduleId?: string;
    unscheduled?: boolean;
    term?: {
      startedAt?: number;
      endedAt?: number;
    };
    termTypes?: string[];
  };
}

interface Props {
  selectItemHandler: (item: UnfixedTicket | FixedTicket) => void;
  selectedMonth: Option;
  productIdFilter: string;
}

function BookingListView({
  selectItemHandler, // 자세히 보기 모달 핸들러
  selectedMonth,
  productIdFilter,
}: Props) {
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedTicket, setSelectedTicket] = useState<
    FixedTicket | UnfixedTicket | null
  >(null); // 접고 열때 필요.

  const [selectedBookingList, setSelectedBookingList] = useState<BookingEdge[]>(
    [],
  );

  const hostId = useUserInformation(state => state.userInfo.me.host.id);
  const client = useApolloClient();

  const unfixedStatusIn = useMemo(
    () => [
      TicketState.CONFIRMED,
      TicketState.USED,
      TicketState.ABSENT,
      TicketState.APPLIED,
    ],
    [],
  );

  const selectedDate = dayjs(new Date());
  const startOfMonth = selectedDate.startOf('month').valueOf();
  const endOfMonth = selectedDate.endOf('month').valueOf();

  const [ticketFilterInput, setTicketFilterInput] = useState<TicketFilterInput>(
    {
      page: 1,
      size: 100,
      filter: {
        hostId: hostId,
        orderType: TicketOrderType.SCHEDULE_EARLIEST,
        termWithType: {
          term: { startedAt: startOfMonth, endedAt: endOfMonth },
          type: BookingTermType.STARTING,
        },
        productId: productIdFilter,
        statusIn: unfixedStatusIn,
        customerSearchKeyword: '',
      },
    },
  );

  const [accumulatedEdges, setAccumulatedEdges] = useState<any[]>([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const allPagesLoadedRef = useRef(false); // 첫 페이지 이후 전체 로드가 끝났는지
  const fetchGenerationRef = useRef(0);
  const isFetchingMoreRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const selectedDate = dayjs(parseInt(selectedMonth.value));
    const startOfMonth = selectedDate.startOf('month').valueOf();
    const endOfMonth = selectedDate.endOf('month').valueOf();

    /* 페이지 리셋 */
    abortControllerRef.current?.abort();
    fetchGenerationRef.current += 1;
    allPagesLoadedRef.current = false;
    setAccumulatedEdges([]);
    isFetchingMoreRef.current = false;
    setIsFetchingMore(false);

    setTicketFilterInput({
      ...ticketFilterInput,
      filter: {
        ...ticketFilterInput.filter,
        productId: productIdFilter,
        termWithType: {
          term: { startedAt: startOfMonth, endedAt: endOfMonth },
          type: BookingTermType.STARTING,
        },
      },
    });
  }, [selectedMonth]);

  const {
    data: bookingTicketsData,
    refetch: refetchUnFixedScheduleTicketList,
    loading: bookingTicketsDataLoading,
  } = useQuery<BookingTicket>(GET_LIST_VIEW_TICKET_LIST, {
    variables: {
      ...ticketFilterInput,
      filter: {
        ...ticketFilterInput.filter,
        statusIn: unfixedStatusIn,
      },
    },
    skip: !hostId,
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (!allPagesLoadedRef.current) {
        setAccumulatedEdges(data?.booking?.tickets?.edges || []);
      }
    },
  });

  // client.query 사용으로 캐시 영향 없이 나머지를 한 번에 로드 (페이지 분할 시 누락/중복 이유는 fetchAllAtOnce 참고)
  const fetchAllPages = useCallback(async () => {
    if (!bookingTicketsData?.booking?.tickets?.pageInfo?.hasNextPage) return;
    if (isFetchingMoreRef.current) return;

    const generation = fetchGenerationRef.current;
    const isStale = () => fetchGenerationRef.current !== generation;

    // 이전 fetchAllPages 요청들 취소
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);

    try {
      const edges = await fetchAllAtOnce(
        bookingTicketsData?.booking?.tickets,
        isStale,
        async (page, size) => {
          const result = await fetchWithRetry(() =>
            client.query<BookingTicket>({
              query: GET_LIST_VIEW_TICKET_LIST,
              variables: {
                ...ticketFilterInput,
                page,
                size,
                filter: {
                  ...ticketFilterInput.filter,
                  statusIn: unfixedStatusIn,
                },
              },
              fetchPolicy: 'network-only',
            }),
          );
          return result.data?.booking?.tickets;
        },
      );

      if (!isStale()) {
        setAccumulatedEdges(edges);
        allPagesLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  }, [bookingTicketsData, client, ticketFilterInput, unfixedStatusIn]);

  const fetchAllPagesRef = useRef(fetchAllPages);
  fetchAllPagesRef.current = fetchAllPages;

  useEffect(() => {
    if (
      bookingTicketsData?.booking?.tickets?.pageInfo?.hasNextPage &&
      !isFetchingMore &&
      !allPagesLoadedRef.current
    ) {
      fetchAllPagesRef.current();
    }
  }, [bookingTicketsData, isFetchingMore]);

  const handleRefetch = useCallback(async () => {
    abortControllerRef.current?.abort();
    fetchGenerationRef.current += 1;
    allPagesLoadedRef.current = false;
    setAccumulatedEdges([]);
    isFetchingMoreRef.current = false;
    setIsFetchingMore(false);
    await refetchUnFixedScheduleTicketList({
      ...ticketFilterInput,
      page: 1,
      filter: {
        ...ticketFilterInput.filter,
        statusIn: unfixedStatusIn,
      },
    });
  }, [refetchUnFixedScheduleTicketList, ticketFilterInput, unfixedStatusIn]);

  const groupDataByDate = (data: FixedTicketEdge[] | TicketEdge[]) => {
    let result = [];

    for (let idx = 0; idx < data?.length; idx++) {
      const isUnfixedTicket = 'scheduleTerm' in data[idx].node ? true : false;

      const dataNode = data[idx].node;
      const dataKey = isUnfixedTicket ? 'scheduleTerm' : 'term';

      const dateString = dataNode[dataKey]
        ? convertTimestampToDate(dataNode[dataKey].startedAt)
        : '';

      // result에서 해당 날짜가 존재하는지 확인
      const existingEntryIndex = result.findIndex(
        item => item.date === dateString,
      );

      if (existingEntryIndex !== -1) {
        // 이미 존재하는 날짜일 경우 value에 새로운 데이터를 추가
        result[existingEntryIndex].value.push(dataNode);
      } else {
        // 존재하지 않는 날짜일 경우 새로운 항목 추가
        result = [
          ...result,
          {
            date: dateString,
            value: [dataNode],
          },
        ];
      }
    }

    return result;
  };

  const bookingTickets =
    groupDataByDate(
      accumulatedEdges.length > 0
        ? accumulatedEdges
        : bookingTicketsData?.booking?.tickets?.edges,
    ) || [];

  let mappedBookingTickets = [];

  for (let item of bookingTickets) {
    let tempMap: { [key: string]: UnfixedTicket[] } = {};

    for (let i = 0; i < item.value.length; i++) {
      const ticket = item.value[i];

      const isFixed = !!ticket.bookingItem?.booking?.schedule;

      const productId = ticket.bookingItem?.booking?.info?.product?.id;

      const startTimeStamp = ticket.scheduleTerm?.startedAt;

      if (isFixed) {
        const fixedTicketScheduleId = ticket.bookingItem?.booking?.schedule?.id;

        if (
          !tempMap[`${productId}-${startTimeStamp}-${fixedTicketScheduleId}`]
        ) {
          tempMap[`${productId}-${startTimeStamp}-${fixedTicketScheduleId}`] =
            [];
        }

        tempMap[`${productId}-${startTimeStamp}-${fixedTicketScheduleId}`].push(
          ticket,
        );
      } else {
        if (!tempMap[`${productId}-${startTimeStamp}`]) {
          tempMap[`${productId}-${startTimeStamp}`] = [];
        }

        tempMap[`${productId}-${startTimeStamp}`].push(ticket);
      }
    }

    Object.values(tempMap).forEach(ticketItem => {
      const isFixed = !!ticketItem[0].bookingItem?.booking?.schedule;

      const valueList = [];

      if (isFixed) {
        ticketItem.forEach(ticket => {
          valueList.push({
            ...ticket,
            exposedCounts: ticketItem.length,
          });
        });
      } else {
        valueList.push({
          ...ticketItem[0],
          exposedCounts: ticketItem.length,
        });
      }

      mappedBookingTickets.push({
        date: item.date,
        value: valueList,
      });
    });
  }

  const openBookingDetailHandler = (ticket: UnfixedTicket | FixedTicket) => {
    if (selectedTicket === null) {
      /* 첫 클릭일 경우 바로 선택된 티켓 저장 */
      return setSelectedTicket(ticket);
    }

    /* 데이터 요청중에는 불가능하게 */
    if (!bookingListLoading && selectedTicket) {
      setSelectedBookingList([]); // 이미 가지고 있던 데이터 초기화 후 새로 조회

      setSelectedTicket(pre => {
        // const isUnfixedTicket = 'scheduleTerm' in
        if (pre === null) {
          return ticket;
        }

        if (pre.id === ticket.id) {
          return null;
        }

        return ticket;
      });
    }
  };

  const [bookingItemsFilter, setBookingItemsFilter] =
    useState<BookingItemsFilter>({
      page: 1,
      size: 200,
      filter: {
        hostId: hostId,
        customerSearchKeyword: '',
      },
    });

  const {
    data,
    loading: bookingListLoading,
    refetch: bookingListRefetch,
  } = useQuery<{
    booking: { bookings: BookingConnection };
  }>(GET_BOOKING_LIST, {
    variables: {
      ...bookingItemsFilter,
    },
    notifyOnNetworkStatusChange: true,
  });

  const keywordHandler = (value: string) => {
    setSearchKeyword(value);
  };

  const refetchFixedBookingList = useCallback(async () => {
    const isFixedTicket = 'minimumQuota' in selectedTicket;

    let ticketFilter = {};

    if (isFixedTicket) {
      ticketFilter = {
        ...bookingItemsFilter,
        filter: {
          ...bookingItemsFilter.filter,
          scheduleId: (selectedTicket as FixedTicket).id,
          customerSearchKeyword: searchKeyword,
          // productIds: [(selectedTicket as FixedTicket).product.id],
          // termTypes: 'STARTING',
          // term: {
          //   startedAt: (selectedTicket as FixedTicket).term.startedAt,
          //   endedAt: (selectedTicket as FixedTicket).term.startedAt,
          // },
        },
      };
    } else {
      ticketFilter = {
        ...bookingItemsFilter,
        filter: {
          ...bookingItemsFilter.filter,
          productIds: [
            (selectedTicket as UnfixedTicket).bookingItem.booking.info.product
              .id,
          ],
          termTypes: 'STARTING',
          term: {
            startedAt: (selectedTicket as UnfixedTicket).scheduleTerm.startedAt,
            endedAt: (selectedTicket as UnfixedTicket).scheduleTerm.endedAt,
          },
          customerSearchKeyword: searchKeyword,
        },
      };
    }

    try {
      const res = await bookingListRefetch({
        ...ticketFilter,
      });

      setSelectedBookingList([...res.data.booking.bookings.edges]);
      // fixedBookingMap[fixedTicket.product.id] = res.data.booking.bookings.edges;
    } catch (error) {
      console.log('error', error);
    }
  }, [selectedTicket, searchKeyword, bookingItemsFilter, bookingListRefetch]);

  const handleSearchBookingList = (keyword?: string) => {
    const effectiveKeyword = keyword ?? searchKeyword;

    setSelectedBookingList([]);

    /* 페이지 리셋 */
    abortControllerRef.current?.abort();
    fetchGenerationRef.current += 1;
    allPagesLoadedRef.current = false;
    setAccumulatedEdges([]);
    isFetchingMoreRef.current = false;
    setIsFetchingMore(false);

    /* 티켓 필터 state 업데이트 */
    setTicketFilterInput({
      ...ticketFilterInput,
      filter: {
        ...ticketFilterInput.filter,
        customerSearchKeyword: effectiveKeyword,
      },
    });

    /* 부킹 필터 state 업데이트 */
    setBookingItemsFilter({
      ...bookingItemsFilter,
      filter: {
        ...bookingItemsFilter.filter,
        customerSearchKeyword: effectiveKeyword,
      },
    });

    refetchUnFixedScheduleTicketList({
      ...ticketFilterInput,
      page: 1,
      filter: {
        ...ticketFilterInput.filter,
        customerSearchKeyword: effectiveKeyword,
      },
    });
  };

  const initSelectedTicket = () => {
    setSelectedTicket(null);
  };

  useEffect(() => {
    if (selectedTicket) {
      refetchFixedBookingList();
    }
  }, [selectedTicket, refetchFixedBookingList]);

  useEffect(() => {
    /* 새로운 검색이 되면 기존에 불러왔던 예약정보 초기화 */
    initSelectedTicket();
  }, [bookingItemsFilter.filter.customerSearchKeyword, productIdFilter]);

  useEffect(() => {
    /* 필터 적용시 */
    abortControllerRef.current?.abort();
    fetchGenerationRef.current += 1;
    allPagesLoadedRef.current = false;
    setAccumulatedEdges([]);
    isFetchingMoreRef.current = false;
    setIsFetchingMore(false);

    refetchUnFixedScheduleTicketList({
      ...ticketFilterInput,
      page: 1,
      filter: {
        ...ticketFilterInput.filter,
        productId: productIdFilter,
      },
    });
  }, [productIdFilter]);

  useEffect(() => {
    document.body.scrollTo({
      top: 0,
    });

    return () => {
      initSelectedTicket();
    };
  }, []);

  return (
    <BookingListContainer>
      {/* 검색 영역 */}
      <SearchInput
        searchKeyword={searchKeyword}
        keywordHandler={keywordHandler}
        handleSearch={handleSearchBookingList}
        placeholder="크루명이나 번호를 입력해 주세요."
        isSearched={
          searchKeyword !== '' &&
          searchKeyword === ticketFilterInput.filter.customerSearchKeyword
        }
        maxWidth="294px"
      />

      {/* 부킹 리스트 영역 */}
      <BookingList>
        {bookingTicketsDataLoading ? (
          <LoadingBox>
            <Loading type="spinner" />
          </LoadingBox>
        ) : (
          <>
            {bookingTickets.length === 0 ? (
              <EmptyList margin="64px 0 0" />
            ) : (
              mappedBookingTickets.map(({ date, value }, idx) => {
                const scheduleTicket = value[0].bookingItem.booking.schedule;
                const isFixedTicket = scheduleTicket;

                return (
                  <li key={`ticket-list-${'fixed'}-${date}-${idx}`}>
                    <ListViewTicketCard
                      ticket={isFixedTicket ? scheduleTicket : value[0]}
                      isFixedTicket={isFixedTicket}
                      date={date}
                      selectedTicket={selectedTicket}
                      selectedBookingList={selectedBookingList}
                      bookingItemsFilter={bookingItemsFilter}
                      openBookingDetailHandler={openBookingDetailHandler}
                      selectItemHandler={selectItemHandler}
                      bookingListLoading={bookingListLoading}
                    />
                  </li>
                );
              })
            )}
          </>
        )}
      </BookingList>

      <RefreshButton handleRefreshData={handleRefetch} />
    </BookingListContainer>
  );
}

export default BookingListView;
